export function setupInput(scene) {
    scene.pointerDownItem = null;
    scene.pointerStartX = 0;
    scene.pointerStartY = 0;

    scene.isDragging = false;
    scene.dragThreshold = scene.dragThreshold ?? 12;

    scene.input.on('pointermove', (pointer) => {
        if (!scene.pointerDownItem || scene.isSwapping || scene.disableInput) {
            return;
        }

        const donut = scene.pointerDownItem;

        const dx = pointer.x - scene.pointerStartX;
        const dy = pointer.y - scene.pointerStartY;

        const distance = Math.max(
            Math.abs(dx),
            Math.abs(dy)
        );

        if (distance < scene.dragThreshold) {
            return;
        }

        scene.isDragging = true;

        updateDonutDragPosition(
            scene,
            donut,
            pointer.x,
            pointer.y
        );
    });

    scene.input.on('pointerup', (pointer) => {
        if (!scene.pointerDownItem) {
            return;
        }

        if (scene.isSwapping || scene.disableInput) {
            clearInputState(scene);
            return;
        }

        const donut = scene.pointerDownItem;

        let didSwap = false;

        if (scene.isDragging) {
            const dx = pointer.x - scene.pointerStartX;
            const dy = pointer.y - scene.pointerStartY;

            const targetDirection = getBestTargetDirection(
                scene,
                donut,
                dx,
                dy
            );

            if (targetDirection) {
                const targetCol =
                    donut.gridCol + targetDirection.col;

                const targetRow =
                    donut.gridRow + targetDirection.row;

                if (
                    targetCol >= 0 &&
                    targetCol < scene.cols &&
                    targetRow >= 0 &&
                    targetRow < scene.rows
                ) {
                    const target =
                        scene.grid[targetRow][targetCol];

                    if (target) {
                        restoreDonutScale(scene, donut);

                        scene.swapItems(
                            donut,
                            target,
                            true
                        );

                        didSwap = true;
                    }
                }
            }
        }

        if (!didSwap) {
            restoreDonutScale(scene, donut);

            scene.moveToGridPosition(
                donut,
                true
            );
        }

        clearInputState(scene);
    });

    scene.input.on('pointerupoutside', () => {
        if (!scene.pointerDownItem) {
            return;
        }

        const donut = scene.pointerDownItem;

        restoreDonutScale(scene, donut);

        scene.moveToGridPosition(
            donut,
            true
        );

        clearInputState(scene);
    });
}


export function attachDonutInput(scene, donut) {
    donut.setInteractive({
        useHandCursor: true
    });

    donut.on('pointerdown', (pointer) => {
        if (
            scene.disableInput ||
            scene.isSwapping ||
            donut.isReturning
        ) {
            return;
        }

        scene.pointerDownItem = donut;

        scene.pointerStartX = pointer.x;
        scene.pointerStartY = pointer.y;

        /*
         * Capture the actual position when grabbed.
         *
         * This is used only for the visual dragging effect.
         * The logical grid position is not changed here.
         */
        donut.dragHomeX = donut.x;
        donut.dragHomeY = donut.y;

        scene.isDragging = false;

        growDonut(scene, donut);
    });
}


/*
 * Determine which adjacent grid position is currently
 * closest to the pointer.
 *
 * This is intentionally recalculated on EVERY pointer move.
 *
 * That means:
 *
 *   down → down
 *   down → left
 *   left → up
 *
 * can all happen naturally while the same donut
 * remains selected.
 */
function getBestTargetDirection(
    scene,
    donut,
    dx,
    dy
) {
    const directions = [];

    /*
     * Horizontal direction.
     */
    if (dx !== 0) {
        const colDirection = dx > 0 ? 1 : -1;
        const targetCol =
            donut.gridCol + colDirection;

        if (
            targetCol >= 0 &&
            targetCol < scene.cols
        ) {
            directions.push({
                axis: 'x',
                col: colDirection,
                row: 0,
                distance: Math.abs(dx)
            });
        }
    }

    /*
     * Vertical direction.
     */
    if (dy !== 0) {
        const rowDirection = dy > 0 ? 1 : -1;
        const targetRow =
            donut.gridRow + rowDirection;

        if (
            targetRow >= 0 &&
            targetRow < scene.rows
        ) {
            directions.push({
                axis: 'y',
                col: 0,
                row: rowDirection,
                distance: Math.abs(dy)
            });
        }
    }

    /*
     * Nothing valid in that direction.
     */
    if (directions.length === 0) {
        return null;
    }

    /*
     * The closest axis to the pointer wins.
     *
     * Because this is recalculated continuously,
     * the donut can naturally switch from X → Y
     * or Y → X while being dragged.
     */
    directions.sort((a, b) => {
        return b.distance - a.distance;
    });

    return directions[0];
}


/*
 * Visually move the selected donut toward the currently
 * closest valid neighboring cell.
 *
 * IMPORTANT:
 * This does NOT modify gridCol/gridRow or scene.grid.
 *
 * It is purely visual.
 */
function updateDonutDragPosition(
    scene,
    donut,
    pointerX,
    pointerY
) {
    const dx =
        pointerX - scene.pointerStartX;

    const dy =
        pointerY - scene.pointerStartY;

    const targetDirection =
        getBestTargetDirection(
            scene,
            donut,
            dx,
            dy
        );

    /*
     * No valid adjacent cell in the direction the
     * pointer is moving.
     *
     * Return visually toward the original position.
     */
    if (!targetDirection) {
        donut.x = donut.dragHomeX;
        donut.y = donut.dragHomeY;
        return;
    }

    if (targetDirection.axis === 'x') {
        const distance =
            Phaser.Math.Clamp(
                Math.abs(dx),
                0,
                scene.cellWidth
            );

        donut.x =
            donut.dragHomeX +
            targetDirection.col * distance;

        donut.y =
            donut.dragHomeY;

        return;
    }

    const distance =
        Phaser.Math.Clamp(
            Math.abs(dy),
            0,
            scene.cellHeight
        );

    donut.y =
        donut.dragHomeY +
        targetDirection.row * distance;

    donut.x =
        donut.dragHomeX;
}


/*
 * Make the selected donut slightly larger.
 */
function growDonut(scene, donut) {
    scene.tweens.killTweensOf(donut);

    if (donut.originalScale == null) {
        donut.originalScale = donut.scale;
    }

    const originalScale =
        donut.originalScale;

    /*
     * 18% larger while selected.
     */
    const selectedScale =
        originalScale * 1.18;

    scene.tweens.add({
        targets: donut,
        scale: selectedScale,
        duration: 120,
        ease: 'Quad.out'
    });
}


/*
 * Return the donut to its normal size.
 */
function restoreDonutScale(scene, donut) {
    scene.tweens.killTweensOf(donut);

    const originalScale =
        donut.originalScale ?? 1;

    scene.tweens.add({
        targets: donut,
        scale: originalScale,
        duration: 120,
        ease: 'Quad.out'
    });
}


function clearInputState(scene) {
    scene.pointerDownItem = null;

    scene.pointerStartX = 0;
    scene.pointerStartY = 0;

    scene.isDragging = false;
}